import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useInRouterContext, useNavigate } from "react-router-dom";

import { DiagnosticSection, type DiagnosticTabProps } from "../../DiagnosticSection";
import { CoaStatusBadge } from "../../chartOfAccounts/CoaStatusBadge";
import { useCoaActionLabels, useCoaStatusLabels } from "../../chartOfAccounts/coaLabels";
import {
  COA_ACTIONS,
  COA_STATUS_COLORS,
  COA_STATUSES,
  chartOfAccountsEnvelope,
  chartOfAccountsFrame,
  formatPercent,
  type ChartOfAccountsFrame,
} from "../../selectors/chartOfAccounts";
import { Donut } from "../../primitives/Donut";
import { NO_VALUE } from "../../sectionState";
import { useCurrentUser } from "@/hooks/useAuth";
import { formatCount } from "@/lib/format";
import { formatDateTime } from "@/lib/datetime";
import { hasMinRole } from "@/lib/roles";

/** O slug do drill-down em `DIAGNOSTIC_DETAILS`, relativo ao Diagnóstico. */
const DETAIL = "chart-of-accounts";

/**
 * "Aderência ao plano de contas" (#1374 P2, EF-FUNC-009 §8), numa linha própria
 * logo abaixo da visão geral do Ato 1.
 *
 * O donut é por status, que é da análise: a aderência no centro não muda quando o
 * consultor decide. A decisão aparece nas barras. Status, barra e fatia abrem o
 * drill-down já filtrado. Sem análise, o quadro fica fechado e nunca mostra 0%.
 */
export function ChartOfAccountsCard({ validation, snapshots }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const envelope = chartOfAccountsEnvelope(validation);
  const frame = chartOfAccountsFrame(validation);

  return (
    <div data-testid="chart-of-accounts-card" data-state={envelope.state}>
      <DiagnosticSection
        envelope={envelope}
        title={t("Chart of accounts adherence")}
        subtitle={
          frame
            ? t("{{count}} accounts in the client's chart compared with the Public YCOA", {
                count: formatCount(frame.accounts),
              })
            : undefined
        }
        snapshots={snapshots}
        showsSampling={false}
        actions={frame ? <SeeDetailLink /> : <GenerateLink />}
      >
        {frame && <FrameBody frame={frame} />}
      </DiagnosticSection>
    </div>
  );
}

/** "Ver detalhe": a lista sem filtro. Só existe com dado, e por isso só ele usa rota. */
function SeeDetailLink() {
  const { t } = useTranslation();
  return (
    <Link
      to={DETAIL}
      className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#08254c] hover:underline"
    >
      {t("See detail")}
      <ArrowRight aria-hidden className="h-3 w-3" />
    </Link>
  );
}

/**
 * Sem análise, o consultor tem o caminho para gerá-la: o detalhe traz "Rodar o
 * motor" e "Importar análise". Só dentro de um roteador, porque o quadro fechado
 * também é renderizado fora de um; e só para consultor, que é quem gera.
 */
function GenerateLink() {
  const { t } = useTranslation();
  const inRouter = useInRouterContext();
  const canAct = hasMinRole(useCurrentUser()?.role, "consultant");
  if (!inRouter || !canAct) return null;
  return (
    <Link
      to={DETAIL}
      className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#08254c] hover:underline"
    >
      {t("Generate the analysis")}
      <ArrowRight aria-hidden className="h-3 w-3" />
    </Link>
  );
}

/**
 * Donut, legenda e barras. Fica separado do quadro para o estado fechado não
 * depender de rota: os atos são renderizados também fora de um roteador.
 */
function FrameBody({ frame }: { frame: ChartOfAccountsFrame }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const statusLabels = useCoaStatusLabels();
  const actionLabels = useCoaActionLabels();
  const peak = Math.max(1, ...COA_ACTIONS.map((action) => frame.byAction[action]));

  return (
    <>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <Donut
            size={132}
            thickness={12}
            slices={COA_STATUSES.map((status) => ({
              key: status,
              label: `${status} · ${statusLabels[status]}`,
              color: COA_STATUS_COLORS[status],
              value: frame.byStatus[status],
            }))}
            sliceTitles
            gap={2}
            onSliceClick={(status) => navigate(`${DETAIL}?status=${status}`)}
            ariaLabel={t("Accounts by adherence status")}
          >
            <span
              data-testid="coa-adherence"
              className="text-[22px] font-semibold leading-none text-[#08254c]"
            >
              {frame.adherencePct != null ? formatPercent(frame.adherencePct) : NO_VALUE}
            </span>
            <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.08em] text-[#7b8799]">
              {t("adherence")}
            </span>
          </Donut>

          <ul className="flex min-w-0 flex-col gap-1.5">
            {COA_STATUSES.map((status) => {
              const value = frame.byStatus[status];
              const share = frame.accounts > 0 ? (value / frame.accounts) * 100 : null;
              return (
                <li key={status}>
                  <Link
                    to={`${DETAIL}?status=${status}`}
                    data-testid={`coa-legend-${status}`}
                    className="-mx-1 flex items-center gap-2 rounded px-1 py-0.5 text-[12px] hover:bg-[#f5f8fc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c]"
                  >
                    <CoaStatusBadge status={status} />
                    <span className="min-w-0 flex-1 truncate text-[#08254c]">
                      {statusLabels[status]}
                    </span>
                    <span className="font-mono tabular-nums text-[#08254c]">
                      {formatCount(value)}
                    </span>
                    <span className="w-12 text-right font-mono tabular-nums text-[#7b8799]">
                      {share != null ? formatPercent(share) : NO_VALUE}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
            {frame.actionBasis === "curation"
              ? t("Action defined by the consultant")
              : t("Suggested action")}
          </div>
          <div className="flex flex-col gap-2">
            {COA_ACTIONS.map((action) => (
              <Link
                key={action}
                to={`${DETAIL}?decision=${action}`}
                data-testid={`coa-action-${action}`}
                className="-mx-1 flex items-center gap-3 rounded px-1 py-0.5 text-[12px] hover:bg-[#f5f8fc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c]"
              >
                <span className="w-28 shrink-0 truncate text-[#08254c]">
                  {actionLabels[action]}
                </span>
                <span className="h-4 min-w-0 flex-1 rounded-sm bg-[#f2f4f7]">
                  <span
                    className="block h-full rounded-sm bg-[#6d8fc4]"
                    style={{
                      width: `${frame.byAction[action] > 0 ? Math.max(1, (frame.byAction[action] / peak) * 100) : 0}%`,
                    }}
                  />
                </span>
                <span className="w-14 shrink-0 text-right font-mono tabular-nums text-[#08254c]">
                  {formatCount(frame.byAction[action])}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <p className="mt-4 text-[11px] leading-snug text-[#7b8799]">
        {t(
          "The adherence comes from the analysis: the consultant's decision changes the bars, not the percentage.",
        )}{" "}
        <span data-testid="coa-basis" className="text-[#596b85]">
          {frame.actionBasis === "curation" && frame.curationSavedAt
            ? t("Curation saved {{when}}.", { when: formatDateTime(frame.curationSavedAt) })
            : t("No curation saved yet: the bars show the suggested action.")}
        </span>
      </p>
    </>
  );
}
